import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Phone, Mail, User, MessageSquare, Send, Car } from 'lucide-react';
import { carModels, advisorInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    interested_model: '',
    variant: '',
    budget_range: '',
    deposit_amount: '',
    loan_years: '9',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        deposit_amount: formData.deposit_amount ? parseFloat(formData.deposit_amount) : null,
        loan_years: formData.loan_years ? parseInt(formData.loan_years) : null
      };

      const response = await axios.post(`${API}/contact`, submitData);

      if (response.status === 200) {
        toast.success('Terima kasih! Syasyah akan hubungi anda tidak lama lagi.', {
          description: 'Maklumat anda telah berjaya dihantar.',
          duration: 5000,
        });

        // Reset form
        setFormData({
          name: '',
          phone: '',
          email: '',
          interested_model: '',
          variant: '',
          budget_range: '',
          deposit_amount: '',
          loan_years: '9',
          message: ''
        });

        // Also send WhatsApp message
        const whatsappMessage = `[Website] PM PERODUA - Hi Syasyah, saya ${formData.name} (${formData.phone}) telah submit contact form. Saya berminat dengan ${formData.interested_model} ${formData.variant}. Boleh contact saya?`;
        window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Maaf, ada masalah dengan sistem. Sila cuba lagi atau WhatsApp terus.', {
        description: 'Atau boleh terus WhatsApp Syasyah untuk bantuan pantas.',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getModelOptions = () => {
    return carModels.map(model => (
      <SelectItem key={model.id} value={model.id}>
        {model.name}
      </SelectItem>
    ));
  };

  const getVariantOptions = () => {
    if (!formData.interested_model) return [];
    const model = carModels.find(m => m.id === formData.interested_model);
    return model.variants.map(variant => (
      <SelectItem key={variant.name} value={variant.name}>
        {variant.name}
      </SelectItem>
    ));
  };

  return (
    <section className="py-20 bg-gradient-to-br from-green-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <MessageSquare className="w-4 h-4" />
            <span>Contact Form</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Berminat Dengan 
            <span className="text-green-600"> Perodua?</span>
          </h2>
          <p className="text-xl text-slate-600">
            Isi form di bawah dan Syasyah akan hubungi anda dalam masa 24 jam
          </p>
        </div>

        <Card className="bg-white shadow-2xl rounded-2xl border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-green-600 to-green-700 text-white p-8">
            <CardTitle className="text-2xl font-bold flex items-center space-x-3">
              <Car className="w-8 h-8" />
              <span>Maklumat Inquiry</span>
            </CardTitle>
            <p className="text-green-100 mt-2">
              Lengkapkan maklumat dan dapatkan quotation terbaik dari Syasyah
            </p>
          </CardHeader>
          
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 flex items-center space-x-2">
                    <User className="w-4 h-4" />
                    <span>Nama Penuh *</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="Nama lengkap anda"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 flex items-center space-x-2">
                    <Phone className="w-4 h-4" />
                    <span>Nombor Telefon *</span>
                  </Label>
                  <Input
                    type="tel"
                    placeholder="012-345 6789"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                    className="w-full"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>Email (Optional)</span>
                </Label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Car Interest */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Model Berminat
                  </Label>
                  <Select value={formData.interested_model} onValueChange={(value) => handleInputChange('interested_model', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih model Perodua" />
                    </SelectTrigger>
                    <SelectContent>
                      {getModelOptions()}
                    </SelectContent>
                  </Select>
                </div>

                {formData.interested_model && (
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">
                      Variant
                    </Label>
                    <Select value={formData.variant} onValueChange={(value) => handleInputChange('variant', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Pilih variant" />
                      </SelectTrigger>
                      <SelectContent>
                        {getVariantOptions()}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Budget Information */}
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Budget Range
                  </Label>
                  <Select value={formData.budget_range} onValueChange={(value) => handleInputChange('budget_range', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih budget" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="below-40k">Bawah RM40,000</SelectItem>
                      <SelectItem value="40k-50k">RM40,000 - RM50,000</SelectItem>
                      <SelectItem value="50k-60k">RM50,000 - RM60,000</SelectItem>
                      <SelectItem value="60k-70k">RM60,000 - RM70,000</SelectItem>
                      <SelectItem value="70k-80k">RM70,000 - RM80,000</SelectItem>
                      <SelectItem value="above-80k">Atas RM80,000</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Deposit Amount (RM)
                  </Label>
                  <Input
                    type="number"
                    placeholder="10000"
                    value={formData.deposit_amount}
                    onChange={(e) => handleInputChange('deposit_amount', e.target.value)}
                    className="w-full"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Tempoh Loan
                  </Label>
                  <Select value={formData.loan_years} onValueChange={(value) => handleInputChange('loan_years', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 Tahun</SelectItem>
                      <SelectItem value="9">9 Tahun</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Message */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 flex items-center space-x-2">
                  <MessageSquare className="w-4 h-4" />
                  <span>Mesej Tambahan (Optional)</span>
                </Label>
                <Textarea
                  placeholder="Apa-apa soalan atau keperluan khas..."
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  className="w-full h-24 resize-none"
                />
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <Button
                  type="submit"
                  disabled={isSubmitting || !formData.name || !formData.phone}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Menghantar...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <Send className="w-5 h-5" />
                      <span>Hantar Inquiry</span>
                    </div>
                  )}
                </Button>
              </div>

              {/* Alternative Contact */}
              <div className="text-center pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-600 mb-3">
                  Atau untuk respon lebih pantas:
                </p>
                <Button
                  type="button"
                  onClick={() => window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent('[Website] PM PERODUA - Hi Syasyah, saya dari website anda dan nak inquiry tentang kereta Perodua.')}`, '_blank')}
                  variant="outline"
                  className="border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white font-medium px-8 py-2 rounded-lg transition-all"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  WhatsApp Syasyah Terus
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ContactForm;